<template>
  <v-dialog v-model="open" max-width="620">
    <v-card>
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-account-group</v-icon>
        <span class="text-h6">{{ title }}</span>
        <v-spacer></v-spacer>
        <v-chip small dark color="primary">{{ students.length }}</v-chip>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="isLoading" indeterminate color="primary" class="my-4" />

        <v-simple-table v-else-if="students.length">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('student_id') }}</th>
                <th class="text-left">{{ $t('Name') }}</th>
                <th class="text-left">{{ $t('Status') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="student in students" :key="student.id">
                <td>{{ student.student_identification }}</td>
                <td>{{ student.name }}</td>
                <td>
                  <v-chip x-small dark :color="statusColor(student.status_id)">
                    {{ statusText(student.status_id) }}
                  </v-chip>
                </td>
                <td class="text-right">
                  <v-icon small @click="viewStudent(student)">mdi-eye</v-icon>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <p v-else class="my-4">{{ $t('no_students_here') }}</p>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="students.length"
          text
          small
          @click="exportStudents"
        >
          <v-icon small class="me-1">mdi-download</v-icon>
          {{ $t('export_csv') }}
        </v-btn>
        <!-- the stop is being moved, the bus is off the road: everyone
             standing here goes somewhere else, in one go rather than one at a
             time through the student list -->
        <v-btn
          v-if="students.length"
          text
          small
          color="primary"
          @click="moveDialog = true"
        >
          <v-icon small class="me-1">mdi-map-marker-path</v-icon>
          {{ $t('move_them_all') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="open = false">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>

    <map-students-dialog
      v-model="moveDialog"
      :students="students"
      @mapped="onMoved"
      @partial-failure="onMoveFailures"
    ></map-students-dialog>
  </v-dialog>
</template>

<script>
/*
 * The children mapped to one route, bus, stop or trip.
 *
 * Every setup table listed containers - a route, a bus, a stop - without ever
 * saying who was inside them, so "who is on this bus?" could only be answered
 * by opening students one at a time. The tables show a count and open this.
 *
 *   <students-on-dialog v-model="dialog" scope="bus" :subject="bus" />
 */
import { exportRowsToCsv } from "@/utils/download";
import MapStudentsDialog from "@/views/users/map-students-dialog";

export default {
  name: "StudentsOnDialog",
  components: { MapStudentsDialog },
  props: {
    value: { type: Boolean, default: false },
    //route | stop | bus | trip
    scope: { type: String, required: true },
    //the row that was clicked; needs an id, and a name or licence to title with
    subject: { type: Object, default: null },
  },
  data() {
    return {
      students: [],
      isLoading: false,
      moveDialog: false,
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    subjectLabel() {
      if (!this.subject) return "";
      return this.subject.name || this.subject.license || `#${this.subject.id}`;
    },
    title() {
      return this.$t("students_on", { name: this.subjectLabel });
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.load();
    },
  },
  methods: {
    load() {
      if (!this.subject) return;

      this.isLoading = true;
      this.students = [];
      axios
        .get("/school-students/assigned", {
          params: { scope: this.scope, id: this.subject.id },
        })
        .then((response) => {
          this.students = (response.data && response.data.students) || [];
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_users"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    onMoved() {
      this.$notifySuccess(this.$t("students_moved"));
      this.moveDialog = false;
      //where they are now is what this dialog is for, so ask again
      this.load();
      this.$emit("moved");
    },
    onMoveFailures(failed) {
      this.$swal({
        title: this.$t("some_students_were_not_mapped"),
        html: (failed || []).map((row) => row.name + ": " + row.message).join("<br>"),
        icon: "warning",
      });
    },
    statusText(statusId) {
      return this.$t(
        { 1: "active", 2: "pending", 3: "suspended", 4: "under_review", 5: "out_of_coins" }[
          statusId
        ] || "active"
      );
    },
    statusColor(statusId) {
      return { 1: "success", 2: "warning", 3: "error", 4: "error", 5: "error" }[statusId] || "success";
    },
    viewStudent(student) {
      this.$router.push({ name: "view-student", params: { user_id: student.id } });
    },
    exportStudents() {
      exportRowsToCsv(
        this.students,
        [
          { text: this.$t("student_id"), value: "student_identification" },
          { text: this.$t("Name"), value: "name" },
          { text: this.$t("Status"), value: (s) => this.statusText(s.status_id) },
        ],
        `${this.scope}-${this.subjectLabel}-students`
      );
    },
  },
};
</script>
